import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import type { BillingSummary, Page, PlanKey } from '@outfalak/shared'
import { PLANS } from '@outfalak/shared'
import { api } from '../lib/api.js'
import { isEmbedded, openTopLevel } from '../lib/shopify.js'
import { ago, dateTime, humanise, money, moneyExact, percent } from '../lib/format.js'
import {
  Badge,
  Card,
  Drawer,
  DrawerSection,
  Empty,
  Field,
  KeyValue,
  Notice,
  PageHead,
  Pager,
  RowsSkeleton,
  Stat,
  StatsSkeleton,
  SettingRow,
  Switch,
  Ticks,
} from '../components/ui.js'
import {
  ArrowRightIcon,
  BellIcon,
  CardIcon,
  CheckIcon,
  CheckCircleIcon,
  ClockIcon,
  DownloadIcon,
  PlusIcon,
  ReceiptIcon,
  RupeeIcon,
  TrendUpIcon,
  WalletIcon,
} from '../components/icons.js'

const PER_PAGE = 25

/**
 * What `/v1/wallet` and `/v1/wallet/transactions` answer with.
 *
 * Money is a decimal string the whole way from Postgres NUMERIC to the screen —
 * see `money()` / `moneyExact()`, which format from the string rather than
 * parsing it into a double first.
 */
interface WalletSummary {
  balance: string
  currency: string
  lowSince: string | null
  minBalance: string
  totalAdded: string
  totalSpent: string
}

interface WalletTransaction {
  id: string
  reference: string
  at: string
  kind: string
  type: 'debit' | 'credit'
  status: string
  amount: string
  balanceAfter: string
  currency: string
  description: string | null
  gateway: string | null
}

/** Ledger kinds, as the API spells them, mapped to how the table reads. */
const KIND_LABEL: Record<string, string> = {
  commission: 'Commission',
  recharge: 'Money added',
  adjustment: 'Adjustment',
  reversal: 'Reversal',
  notification: 'Notification fee',
}

function TransactionRows({ rows, currency }: { rows: WalletTransaction[]; currency: string }): React.ReactNode {
  return (
    <>
      {rows.map((row) => (
        <tr key={row.id}>
          <td>
            {dateTime(row.at).split(',')[0]}
            <span className="table__sub">{ago(row.at)}</span>
          </td>
          <td>
            {KIND_LABEL[row.kind] ?? humanise(row.kind)}
            {row.gateway && <span className="table__sub">{row.gateway}</span>}
          </td>
          <td>
            {row.description ?? '—'}
            <span className="table__sub">
              <code>{row.reference}</code>
            </span>
          </td>
          <td
            className="right num"
            style={{ color: row.type === 'credit' ? 'var(--ok)' : 'var(--bad)' }}
          >
            {row.type === 'credit' ? '+ ' : '− '}
            {moneyExact(row.amount, row.currency || currency)}
          </td>
          <td>
            <Badge tone={row.status === 'settled' ? 'ok' : 'accent'}>{humanise(row.status)}</Badge>
          </td>
          <td className="right num">{moneyExact(row.balanceAfter, row.currency || currency)}</td>
        </tr>
      ))}
    </>
  )
}

// ── Overview ────────────────────────────────────────────────────────────────

export function WalletOverviewPage(): React.ReactNode {
  const summary = useQuery({ queryKey: ['wallet'], queryFn: () => api<WalletSummary>('/v1/wallet') })
  const [params, setParams] = useSearchParams()
  const queryClient = useQueryClient()
  const [settled, setSettled] = useState<{ credited: boolean; status: string; error: string | null } | null>(null)
  // Shopify stores bill exclusively through Shopify's Billing API (App Store
  // requirement 1.2.1). Wallet top-ups over Cashfree are off-platform charges
  // for them, so the Add-money path is hidden here — commission lands on the
  // Shopify invoice instead.
  const embedded = isEmbedded()

  /*
   * The gateway sends the browser back here with the reference it was given.
   *
   * Asking the API what happened rather than reading a status out of the URL:
   * the URL is whatever the merchant's browser was handed, and a top-up is
   * credited on what Cashfree says, never on what the address bar claims. The
   * webhook usually gets there first — this call then simply reports what it
   * already did.
   */
  const reference = params.get('recharge')
  // Set when the top-up was started from "Top up & upgrade" on Billing.
  const pendingPlan = params.get('plan') as PlanKey | null
  const [planSwitch, setPlanSwitch] = useState<{ plan: PlanKey; error: string | null } | null>(null)

  useEffect(() => {
    if (!reference) return

    let cancelled = false

    void api<{ status: string; credited: boolean; balance: string | null; error: string | null }>(
      `/v1/wallet/recharge/${encodeURIComponent(reference)}/settle`,
      { method: 'POST' },
    )
      .then((result) => {
        if (cancelled) return
        setSettled({ credited: result.credited, status: result.status, error: result.error })
        void queryClient.invalidateQueries({ queryKey: ['wallet'] })

        // Money in, so finish what the merchant came for. The API re-checks the
        // balance; a refusal here is shown, never papered over.
        if (pendingPlan && pendingPlan in PLANS && (result.credited || result.status === 'paid')) {
          return api('/v1/billing/plan', { method: 'PUT', body: { plan: pendingPlan } })
            .then(() => {
              if (!cancelled) setPlanSwitch({ plan: pendingPlan, error: null })
            })
            .catch((err: unknown) => {
              if (!cancelled) setPlanSwitch({ plan: pendingPlan, error: (err as Error).message })
            })
            .finally(() => {
              void queryClient.invalidateQueries({ queryKey: ['billing'] })
              void queryClient.invalidateQueries({ queryKey: ['wallet'] })
            })
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setSettled({ credited: false, status: 'error', error: (err as Error).message })
      })
      .finally(() => {
        if (cancelled) return
        // Cleared so a refresh does not look like a second payment.
        const next = new URLSearchParams(params)
        next.delete('recharge')
        next.delete('plan')
        setParams(next, { replace: true })
      })

    return () => {
      cancelled = true
    }
    // Deliberately keyed on the reference alone: re-running because the router
    // handed back a new params object would settle the same top-up twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference])

  const recent = useQuery({
    queryKey: ['wallet', 'transactions', 'recent'],
    queryFn: () => api<Page<WalletTransaction>>('/v1/wallet/transactions', { query: { page: 1, perPage: 5 } }),
  })

  const wallet = summary.data
  const low = wallet?.lowSince !== null && wallet?.lowSince !== undefined

  return (
    <>
      {settled?.credited && (
        <Notice kind="ok" title="Top-up added">
          Your balance has been updated.
        </Notice>
      )}

      {settled && !settled.credited && settled.status === 'paid' && !settled.error && (
        <Notice kind="ok" title="Already added">
          This top-up had already been credited.
        </Notice>
      )}

      {planSwitch && !planSwitch.error && (
        <Notice kind="ok" title={`You are on ${PLANS[planSwitch.plan].name}`}>
          The first month has been taken from your wallet.
        </Notice>
      )}

      {planSwitch?.error && (
        <Notice kind="error" title={`Could not switch to ${PLANS[planSwitch.plan].name}`}>
          {planSwitch.error} <Link to="/wallet/plans">Try again from Plans</Link>.
        </Notice>
      )}

      {settled?.error && (
        <Notice kind="error" title="Could not confirm the top-up">
          {settled.error}
        </Notice>
      )}

      {settled && !settled.credited && !settled.error && settled.status !== 'paid' && (
        <Notice kind="warn" title="Payment not completed">
          The gateway reports this top-up as {settled.status.toLowerCase()}. Nothing has been charged.
        </Notice>
      )}

      <PageHead
        title="Wallet overview"
        subtitle="Your balance, what has been spent, and what is on the way."
        actions={
          <>
            {!embedded && (
              <Link to="/wallet/add-money" className="btn btn--primary">
                <PlusIcon size={16} />
                Recharge wallet
              </Link>
            )}
          </>
        }
      />

      {summary.error && (
        <Card>
          <Empty error title="Could not load your wallet">
            {(summary.error as Error).message}
          </Empty>
        </Card>
      )}

      {summary.isPending && <StatsSkeleton count={4} />}

      {wallet && (
        <>
          {/* The floor is what stops orders, so a wallet under it is the first
              thing on the screen rather than a colour on a tile. */}
          {low && (
            <Notice kind="warn" title="Your balance is below the floor">
              Low since {ago(wallet.lowSince)}. Top up to keep orders settling.
            </Notice>
          )}

          <section className="grid grid--4">
            <Stat
              label="Available balance"
              value={money(wallet.balance, wallet.currency)}
              icon={<WalletIcon size={20} />}
              tone={Number(wallet.balance) < 0 ? 'var(--bad)' : 'var(--brand)'}
              footnote={<Badge tone={low ? 'warn' : 'ok'}>{low ? 'Low' : 'Healthy'}</Badge>}
            />
            <Stat
              label="Total added"
              value={money(wallet.totalAdded, wallet.currency)}
              icon={<TrendUpIcon size={20} />}
              tone="var(--ok)"
              footnote={<span className="faint">all top-ups and credits</span>}
            />
            <Stat
              label="Total spent"
              value={money(wallet.totalSpent, wallet.currency)}
              icon={<CardIcon size={20} />}
              tone="var(--bad)"
              footnote={<span className="faint">commission charged</span>}
            />
            <Stat
              label="Currency"
              value={wallet.currency}
              icon={<RupeeIcon size={20} />}
              tone="var(--accent)"
              footnote={<span className="faint">account currency</span>}
            />
          </section>
        </>
      )}

      <div className="grid grid--main">
        <Card
          title="Recent transactions"
          flush
          action={
            <Link to="/wallet/transactions" className="btn btn--link">
              View all
              <ArrowRightIcon size={15} />
            </Link>
          }
        >
          {recent.isPending && <RowsSkeleton rows={5} />}

          {recent.error && (
            <Empty error title="Could not load transactions">
              {(recent.error as Error).message}
            </Empty>
          )}

          {recent.data && recent.data.items.length === 0 && (
            <Empty icon={<WalletIcon size={24} />} title="No transactions yet">
              Top-ups and commission charges will appear here as they happen.
            </Empty>
          )}

          {recent.data && recent.data.items.length > 0 && (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th className="right">Amount</th>
                    <th>Status</th>
                    <th className="right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  <TransactionRows rows={recent.data.items} currency={wallet?.currency ?? 'INR'} />
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="stack">
          <Card title="Quick actions" flush>
            <ul className="list">
              {!embedded && (
                <li>
                  <span className="feature__icon" style={{ ['--tone' as string]: 'var(--brand)' }}>
                    <PlusIcon size={16} />
                  </span>
                  <span className="list__text">
                    <strong>Recharge wallet</strong>
                    <small>Top up instantly over UPI or card</small>
                  </span>
                  <Link to="/wallet/add-money" className="btn btn--sm">
                    Open
                  </Link>
                </li>
              )}
              <li>
                <span className="feature__icon" style={{ ['--tone' as string]: 'var(--accent)' }}>
                  <ReceiptIcon size={16} />
                </span>
                <span className="list__text">
                  <strong>Billing &amp; invoices</strong>
                  <small>Download invoices and receipts</small>
                </span>
                <Link to="/wallet/billing" className="btn btn--sm">
                  Open
                </Link>
              </li>
            </ul>
          </Card>

          <Card title="How the wallet works" accent>
            <Ticks
              brand
              items={[
                'Commission is debited when an order settles',
                'A refunded order credits its commission back',
                'Orders fail if the balance runs dry — keep a buffer',
              ]}
            />
          </Card>
        </div>
      </div>
    </>
  )
}

// ── Transactions ────────────────────────────────────────────────────────────

export function WalletTransactionsPage(): React.ReactNode {
  const [kind, setKind] = useState('')
  const [type, setType] = useState('')
  const [page, setPage] = useState(1)

  const { data, isPending, error } = useQuery({
    queryKey: ['wallet', 'transactions', kind, type, page],
    queryFn: () =>
      api<Page<WalletTransaction>>('/v1/wallet/transactions', {
        query: { kind, type, page, perPage: PER_PAGE },
      }),
  })

  const summary = useQuery({ queryKey: ['wallet'], queryFn: () => api<WalletSummary>('/v1/wallet') })

  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PER_PAGE))

  function reset(next: () => void): void {
    next()
    setPage(1)
  }

  return (
    <>
      <PageHead
        title="Wallet transactions"
        subtitle="Every credit and debit against your wallet balance."
        crumbs={[{ label: 'Wallet', to: '/wallet' }, { label: 'Transactions' }]}
        actions={
          <button type="button" className="btn" onClick={() => window.print()}>
            <DownloadIcon size={16} />
            Export
          </button>
        }
      />

      <Card flush>
        <div className="toolbar">
          <select className="select" value={kind} onChange={(event) => reset(() => setKind(event.target.value))} aria-label="Kind">
            <option value="">All kinds</option>
            <option value="commission">Commission</option>
            <option value="recharge">Money added</option>
            <option value="adjustment">Adjustment</option>
            <option value="reversal">Reversal</option>
            <option value="notification">Notification fee</option>
          </select>

          <select className="select" value={type} onChange={(event) => reset(() => setType(event.target.value))} aria-label="Direction">
            <option value="">Credits and debits</option>
            <option value="credit">Credits only</option>
            <option value="debit">Debits only</option>
          </select>

          <button type="button" className="btn btn--ghost btn--sm" onClick={() => reset(() => { setKind(''); setType('') })}>
            Clear filters
          </button>
        </div>

        {error && (
          <Empty error title="Could not load transactions">
            {(error as Error).message}
          </Empty>
        )}

        {isPending && <RowsSkeleton rows={8} />}

        {data && data.items.length === 0 && !error && (
          <Empty icon={<WalletIcon size={24} />} title="No transactions match those filters">
            Top-ups and commission charges will appear here as they happen.
          </Empty>
        )}

        {data && data.items.length > 0 && (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th className="right">Amount</th>
                    <th>Status</th>
                    <th className="right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  <TransactionRows rows={data.items} currency={summary.data?.currency ?? 'INR'} />
                </tbody>
              </table>
            </div>

            <Pager page={page} pageCount={pageCount} total={total} perPage={PER_PAGE} onPage={setPage} noun="transactions" />
          </>
        )}
      </Card>
    </>
  )
}
// ── Recharge wallet ───────────────────────────────────────────────────────────────

/** What `/v1/wallet/recharge/options` answers with. */
interface RechargeOptions {
  enabled: boolean
  min: number
  max: number
  presets: number[]
  mode: string
}

/**
 * Cashfree's checkout script, loaded the first time somebody actually pays.
 *
 * Not in index.html: every merchant would fetch a payment SDK on every page
 * load for a screen most of them open once a month. Resolved from a module
 * promise so two clicks cannot inject two copies.
 */
let sdk: Promise<unknown> | null = null

function loadCashfree(): Promise<unknown> {
  if (sdk) return sdk

  sdk = new Promise((resolve, reject) => {
    const existing = (window as unknown as { Cashfree?: unknown }).Cashfree
    if (existing) {
      resolve(existing)
      return
    }

    const script = document.createElement('script')
    script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js'
    script.async = true
    script.onload = () => {
      const loaded = (window as unknown as { Cashfree?: unknown }).Cashfree
      if (loaded) resolve(loaded)
      else reject(new Error('The payment script loaded but did not start.'))
    }
    script.onerror = () => {
      sdk = null // so a retry after a dropped connection can try again
      reject(new Error('Could not load the payment script.'))
    }

    document.head.appendChild(script)
  })

  return sdk
}

export function AddMoneyPage(): React.ReactNode {
  const [params] = useSearchParams()
  // Arrived from "Top up & upgrade": the amount the plan is short by, and the
  // plan to switch to once the payment lands.
  const forPlan = params.get('plan')
  const plan = forPlan === 'professional' || forPlan === 'scale' ? forPlan : null
  const [amount, setAmount] = useState(() => params.get('amount')?.replace(/[^\d.]/g, '') ?? '')
  const [error, setError] = useState<string | null>(null)
  const embedded = isEmbedded()

  const options = useQuery({
    queryKey: ['wallet', 'recharge-options'],
    queryFn: () => api<RechargeOptions>('/v1/wallet/recharge/options'),
  })

  const min = options.data?.min ?? 0
  const max = options.data?.max ?? 0

  // A shortfall under the smallest top-up is lifted to it, so the prefilled
  // amount is always one the gateway will take.
  useEffect(() => {
    if (plan && min > 0 && amount !== '' && Number(amount) < min) setAmount(String(min))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [min])
  const value = Number(amount)
  const valid = Number.isFinite(value) && value >= min && value <= max

  const pay = useMutation({
    mutationFn: async () => {
      const started = await api<{ reference: string; paymentSessionId: string; mode: string }>('/v1/wallet/recharge', {
        method: 'POST',
        body: plan ? { amount: value, plan } : { amount: value },
      })

      const factory = (await loadCashfree()) as (config: { mode: string }) => {
        checkout: (options: { paymentSessionId: string; redirectTarget?: string }) => Promise<unknown>
      }

      // A full redirect rather than a modal: the return URL is what brings the
      // browser back with the reference, and a modal that the merchant closes
      // mid-payment leaves this page with no way to know what happened.
      await factory({ mode: started.mode === 'production' ? 'production' : 'sandbox' }).checkout({
        paymentSessionId: started.paymentSessionId,
        redirectTarget: '_self',
      })
    },
    onError: (err) => setError((err as Error).message),
  })

  return (
    <>
      <PageHead
        title="Recharge wallet"
        subtitle="Top up your wallet so orders never fail for want of balance."
        crumbs={[{ label: 'Wallet', to: '/wallet' }, { label: 'Recharge wallet' }]}
      />

      {embedded && (
        <Notice kind="info" title="Wallet top-ups are disabled for Shopify stores">
          Shopify requires all app charges to go through Shopify billing. Your commission is charged to your Shopify
          invoice — approve it under Billing &amp; invoices instead of topping up here.
        </Notice>
      )}

      {options.data && !options.data.enabled && (
        <Notice kind="warn" title="Top-ups are switched off">
          The platform gateway is not accepting payments right now. Contact support if you need a balance added.
        </Notice>
      )}

      {error && (
        <Notice kind="error" title="Could not start the payment">
          {error}
        </Notice>
      )}

      {plan && (
        <Notice kind="info" title={`Topping up for ${PLANS[plan].name}`}>
          Once the payment lands you will be moved to {PLANS[plan].name} and the first month of{' '}
          {money(Number(PLANS[plan].price))} will be taken from the wallet.
        </Notice>
      )}

      {options.data?.mode === 'sandbox' && (
        <Notice kind="info" title="Test mode">
          The gateway is in sandbox. Payments made here are not real and no money moves.
        </Notice>
      )}

      <div className="grid grid--main">
        <Card title="Amount">
          {options.isPending ? (
            <RowsSkeleton rows={4} />
          ) : (
            <div className="stack">
              <div className="row" style={{ flexWrap: 'wrap' }}>
                {(options.data?.presets ?? []).map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    className={amount === String(preset) ? 'btn btn--primary' : 'btn'}
                    onClick={() => setAmount(String(preset))}
                  >
                    {money(preset)}
                  </button>
                ))}
              </div>

              <Field label="Or enter an amount" hint={`Between ${money(min)} and ${money(max)}.`}>
                <div className="input-group">
                  <RupeeIcon size={16} />
                  <input
                    className="input"
                    inputMode="decimal"
                    value={amount}
                    placeholder={String(min)}
                    onChange={(event) => setAmount(event.target.value.replace(/[^\d.]/g, ''))}
                  />
                </div>
              </Field>

              {/* No payment-method picker: Cashfree's own checkout presents UPI,
                  cards and netbanking, and a selector here would be decoration
                  that never reaches the gateway. */}

              <button
                type="button"
                className="btn btn--primary btn--lg btn--block"
                disabled={!valid || pay.isPending || options.data?.enabled === false || embedded}
                onClick={() => {
                  setError(null)
                  pay.mutate()
                }}
              >
                {pay.isPending ? 'Opening payment…' : `Pay ${valid ? money(value) : '—'}`}
              </button>
            </div>
          )}
        </Card>

        <div className="stack">
          <Card title="Summary">
            <KeyValue
              rows={[
                { label: 'Top-up amount', value: valid ? money(value) : '—' },
                { label: 'Credited to wallet', value: valid ? money(value) : '—', kind: 'total' },
              ]}
            />

            {/*
              No GST line, and that is a decision rather than an omission. A
              top-up is a deposit, not a supply — what is billed is the
              commission taken per settled order, and that already carries GST
              (see Billing & invoices). Adding tax here as well would charge it
              twice on the same money.
            */}
            <p className="muted" style={{ fontSize: '.8rem', marginTop: 10 }}>
              You pay exactly this and exactly this is credited. GST is charged on commission, not on a top-up.
            </p>

            {!valid && amount !== '' && (
              <p className="muted" style={{ fontSize: '.82rem', marginTop: 10 }}>
                Enter between {money(min)} and {money(max)}.
              </p>
            )}
          </Card>

          <Card title="Why keep a balance" accent>
            <Ticks
              brand
              items={[
                'Commission debits settle instantly',
                'A paid plan renews from this balance each month',
                'No order fails for want of balance',
              ]}
            />
          </Card>
        </div>
      </div>
    </>
  )
}

// ── Billing & invoices ──────────────────────────────────────────────────────

/** What `/v1/invoices` answers with. */
interface Invoice {
  period: string
  label: string
  base: string
  tax: string
  total: string
  orders: number
  currency: string
}

interface InvoicesPayload {
  months: number
  invoices: Invoice[]
  lifetime: { base: string; tax: string; total: string; orders: number }
}

interface InvoiceDetail {
  period: string
  label: string
  currency: string
  billedTo: { name: string; gstin: string | null } | null
  base: string
  tax: string
  total: string
  lines: Array<{
    id: string
    reference: string
    at: string
    orderNumber: string | null
    orderValue: string
    rate: string
    chargeType: string | null
    base: string
    tax: string
    amount: string
  }>
}

/**
 * Invoices, derived from the ledger rather than stored.
 *
 * A month's invoice *is* the commission charged that month, so there is no
 * invoices table to drift from the ledger. The consequence is worth stating on
 * screen: these are statements, and a reversal posted later changes the month
 * it belongs to. That is the correct behaviour — the alternative is an invoice
 * that disagrees with the wallet.
 */

/**
 * What Shopify's Billing API answers about this store.
 *
 * Money arrives as decimal strings, the same convention every other wallet
 * figure on this screen uses — a balance through a JS double is how a merchant's
 * total ends up a paisa short in the one place they are checking it.
 */
interface ShopifyBillingState {
  offered: boolean
  connected: boolean
  status: string | null
  active: boolean
  cap: string
  used: string
  currency: string
  activatedAt: string | null
  confirmationUrl: string | null
  test: boolean
}

/**
 * Approving the app charge, and where the commission is collected from.
 *
 * Only rendered inside the Shopify admin. A Shopify merchant has no user row
 * here — Shopify vouching for them is the credential — so `isEmbedded()` is
 * exactly the population this applies to, and a WooCommerce or plain-site
 * merchant never sees a card about a subscription they cannot have.
 *
 * ── Why the merchant has to approve at all ─────────────────────────────────
 *
 * Shopify requires that an app's charges go through their Billing API. Until
 * this is approved the commission comes out of the prepaid wallet; once it is,
 * the same commission lands on the store's Shopify invoice instead and nothing
 * touches the wallet. The switch is the store's own subscription state, so
 * there is nothing for the merchant to configure beyond the approval itself.
 */
function ShopifyBillingCard(): React.ReactNode {
  const client = useQueryClient()
  const embedded = isEmbedded()

  const { data, error } = useQuery({
    queryKey: ['shopify-billing'],
    queryFn: () => api<ShopifyBillingState>('/v1/shopify/billing'),
    enabled: embedded,
    // One failure is the answer. This is a card that either applies or does not,
    // and retrying a store that is not a Shopify store just delays hiding it.
    retry: false,
  })

  const start = useMutation({
    mutationFn: () =>
      api<{ active: boolean; confirmationUrl: string | null; test: boolean }>('/v1/shopify/billing/subscribe', {
        method: 'POST',
      }),
    onSuccess: (result) => {
      void client.invalidateQueries({ queryKey: ['shopify-billing'] })

      // Shopify's approval screen refuses to be framed, so this has to leave the
      // admin's iframe entirely. The merchant comes back to this page.
      if (result.confirmationUrl) openTopLevel(result.confirmationUrl)
    },
  })

  if (!embedded) return null
  if (error) {
    // A card that either applies or does not — but never a blank page. An error
    // here usually means "not a Shopify store", so hide only then. Any other
    // failure is shown, so the reviewer always sees the Shopify billing path.
    return (
      <Card title="Billing through Shopify" subtitle="Approve once, and commission is charged to your Shopify invoice instead of your wallet.">
        <Notice kind="error" title="Could not load Shopify billing">
          {(error as Error).message}
        </Notice>
      </Card>
    )
  }
  if (!data) return null

  // Always visible inside the Shopify admin, even when this deployment is not
  // offering billing yet. Hiding it leaves only the wallet plans on screen —
  // exactly what review flags as off-platform billing — with no compliant path
  // to approve. Showing it with the reason is the honest failure.
  if (!data.offered && !data.active && data.status === null) {
    return (
      <Card
        title="Billing through Shopify"
        subtitle="Approve once, and commission is charged to your Shopify invoice instead of your wallet."
        accent
      >
        <Notice kind="warn" title="Shopify billing is not switched on for this store yet">
          The approval button will appear here once billing is enabled. Contact support if you see this on a live
          store.
        </Notice>
        <button
          type="button"
          className="btn btn--primary"
          disabled={start.isPending}
          onClick={() => start.mutate()}
        >
          {start.isPending ? 'Asking Shopify…' : 'Approve billing on Shopify'}
        </button>
        {start.error && (
          <Notice kind="error" title="Shopify would not set this up">
            {(start.error as Error).message}
          </Notice>
        )}
      </Card>
    )
  }

  const pending = data.status === 'pending' && data.confirmationUrl !== null
  const lapsed = data.status !== null && !data.active && !pending

  return (
    <Card
      title="Billing through Shopify"
      subtitle={
        data.active
          ? 'Your commission is charged to your Shopify invoice.'
          : 'Approve once, and commission is charged to your Shopify invoice instead of your wallet.'
      }
      accent={!data.active}
    >
      <KeyValue
        rows={[
          {
            label: 'Status',
            value: (
              <Badge tone={data.active ? 'ok' : pending ? 'accent' : 'warn'}>
                {data.active ? 'Active' : pending ? 'Awaiting your approval' : humanise(data.status ?? 'not set up')}
              </Badge>
            ),
          },
          { label: 'Used this period', value: moneyExact(data.used, data.currency) },
          { label: 'Approved limit', value: moneyExact(data.cap, data.currency) },
          ...(data.activatedAt ? [{ label: 'Approved on', value: dateTime(data.activatedAt) }] : []),
        ]}
      />

      {data.test && (
        // Said out loud because it is invisible otherwise, and a rehearsal that
        // is quietly billing for real is the failure worth preventing.
        <Notice kind="info" title="Test charges">
          Charges from this store are recorded by Shopify and never billed. This is what a development store always gets.
        </Notice>
      )}

      {!data.active && !data.connected && (
        <Notice kind="warn" title="This store is not connected">
          Open Outfalak Checkout from your Shopify admin to reconnect, then approve billing here.
        </Notice>
      )}

      {lapsed && (
        <Notice kind="warn" title="Commission is coming out of your wallet">
          Your Shopify subscription is {humanise(data.status ?? '')}. Until it is approved again, each order&rsquo;s commission
          is debited from your wallet balance.
        </Notice>
      )}

      {start.error && (
        <Notice kind="error" title="Shopify would not set this up">
          {(start.error as Error).message}
        </Notice>
      )}

      {!data.active && data.connected && (
        <button
          type="button"
          className="btn btn--primary"
          disabled={start.isPending}
          onClick={() => {
            // A pending approval is not re-created — the API hands the same link
            // back — so this button is safe to press twice. It opens the screen
            // the merchant already has waiting.
            if (data.confirmationUrl) {
              openTopLevel(data.confirmationUrl)
              return
            }

            start.mutate()
          }}
        >
          {start.isPending ? 'Asking Shopify…' : pending ? 'Finish approving on Shopify' : 'Approve billing on Shopify'}
        </button>
      )}
    </Card>
  )
}
export function BillingPage(): React.ReactNode {
  const [open, setOpen] = useState<string | null>(null)
  // Shopify embedded admin: paid plans and wallet top-ups are off-platform
  // billing (requirement 1.2.1) and are hidden here. The only paid path is the
  // Shopify subscription below.
  const embedded = isEmbedded()

  const { data, isPending, error } = useQuery({
    queryKey: ['invoices'],
    queryFn: () => api<InvoicesPayload>('/v1/invoices', { query: { months: 12 } }),
  })

  const billing = useQuery({ queryKey: ['billing'], queryFn: () => api<BillingSummary>('/v1/billing') })

  const detail = useQuery({
    queryKey: ['invoice', open],
    queryFn: () => api<InvoiceDetail>(`/v1/invoices/${open!.slice(0, 7)}`),
    enabled: open !== null,
  })

  const rate = billing.data?.commission
  // A plan fee above zero is what this merchant's orders actually carry — it
  // outranks the platform commission — so it is the rate to show.
  const planRate = billing.data?.planCharge?.rate ?? 0
  const rateLabel = !rate
    ? '—'
    : planRate > 0
      ? percent(planRate)
      : !rate.enabled
      ? 'Not charged'
      : rate.type === 'percent'
        ? percent(rate.amount)
        : moneyExact(rate.amount)

  return (
    <>
      <PageHead
        title="Billing &amp; invoices"
        subtitle="Your commission, month by month."
        crumbs={[{ label: 'Wallet', to: '/wallet' }, { label: 'Billing & invoices' }]}
        actions={
          <button type="button" className="btn" onClick={() => window.print()}>
            <DownloadIcon size={16} />
            Export
          </button>
        }
      />

      {error && (
        <Card>
          <Empty error title="Could not load your invoices">
            {(error as Error).message}
          </Empty>
        </Card>
      )}

      {isPending && <StatsSkeleton count={4} />}

      {data && (
        <section className="grid grid--4">
          <Stat
            label="Charged (12 months)"
            value={money(data.lifetime.total)}
            icon={<ReceiptIcon size={20} />}
            tone="var(--brand)"
          />
          <Stat label="Commission" value={money(data.lifetime.base)} icon={<RupeeIcon size={20} />} tone="var(--accent)" />
          <Stat label="GST" value={money(data.lifetime.tax)} icon={<ReceiptIcon size={20} />} tone="var(--warn)" />
          <Stat
            label="Current rate"
            value={rateLabel}
            icon={<CardIcon size={20} />}
            tone="var(--ok)"
            footnote={<span className="faint">{rate?.paidBy === 'customer' ? 'customer pays' : 'from your wallet'}</span>}
          />
        </section>
      )}

      <div className="grid grid--main">
        <Card title="Monthly statements" subtitle="Click a month to see the charges behind it." flush>
          {isPending && <RowsSkeleton rows={6} />}

          {data && data.invoices.length === 0 && (
            <Empty icon={<ReceiptIcon size={24} />} title="Nothing billed yet">
              A statement appears here for every month in which commission was charged.
            </Empty>
          )}

          {data && data.invoices.length > 0 && (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th className="right">Orders</th>
                    <th className="right">Commission</th>
                    <th className="right">GST</th>
                    <th className="right">Total</th>
                    <th aria-label="Open" />
                  </tr>
                </thead>
                <tbody>
                  {data.invoices.map((invoice) => (
                    <tr key={invoice.period} className="is-clickable" onClick={() => setOpen(invoice.period)}>
                      <td className="table__id">{invoice.label}</td>
                      <td className="right num">{invoice.orders.toLocaleString('en-IN')}</td>
                      <td className="right num">{moneyExact(invoice.base, invoice.currency)}</td>
                      <td className="right num">{moneyExact(invoice.tax, invoice.currency)}</td>
                      <td className="right num">{moneyExact(invoice.total, invoice.currency)}</td>
                      <td>
                        <button
                          type="button"
                          className="icon-btn"
                          aria-label={`Open ${invoice.label}`}
                          onClick={(event) => {
                            event.stopPropagation()
                            setOpen(invoice.period)
                          }}
                        >
                          <ReceiptIcon size={17} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="stack">
          {!embedded && billing.data && (
            <Card title="Your plan" action={<Link to="/wallet/plans">Compare plans</Link>}>
              <KeyValue
                rows={[
                  {
                    label: 'Plan',
                    value: `${billing.data.plan.name} · ${Number(billing.data.plan.price) > 0 ? `${money(Number(billing.data.plan.price))}/month` : 'Free'}`,
                  },
                  ...(billing.data.renewsAt ? [{ label: 'Renews', value: dateTime(billing.data.renewsAt).split(',')[0] }] : []),
                ]}
              />
            </Card>
          )}

          {embedded && (
            <Notice kind="info" title="One plan for Shopify stores">
              Shopify stores run on a single plan billed through Shopify — there are no paid tiers to switch between
              here. Approve billing below and commission is charged to your Shopify invoice.
            </Notice>
          )}

          <Card title="How you are billed">
            <KeyValue
              rows={[
                { label: 'Commission rate', value: rateLabel },
                {
                  label: `Plan fee (${billing.data?.plan.name ?? 'Starter'})`,
                  value:
                    billing.data && billing.data.planCharge.rate > 0
                      ? percent(billing.data.planCharge.rate)
                      : 'Falls back to commission',
                },
                { label: 'GST', value: rate ? percent(rate.taxPercent, 0) : '—' },
                {
                  label: 'Paid by',
                  value: embedded ? 'Your Shopify invoice' : rate?.paidBy === 'customer' ? 'Customer' : 'Your wallet',
                },
                { label: 'Prepaid orders', value: rate?.appliesToPrepaid ? 'Charged' : 'Not charged' },
                { label: 'COD orders', value: rate?.appliesToCod ? 'Charged' : 'Not charged' },
              ]}
            />
          </Card>

          <ShopifyBillingCard />

          {/* Stated plainly because it is a real property of deriving invoices
              rather than storing them, and a merchant reconciling last month
              needs to know it. */}
          <Notice kind="info" title="These are statements, not fixed documents">
            Each month is the commission actually charged in it. If a charge is reversed later, the month it belonged to changes
            to match your wallet — which is the point: the statement can never disagree with the ledger.
          </Notice>

          <Card title="What commission covers" accent>
            <Ticks
              brand
              items={
                embedded
                  ? [
                      'Charged on settled orders only — a failed or cancelled order costs nothing',
                      'A refunded order credits its commission back',
                      'Charged to your Shopify invoice as each order settles',
                    ]
                  : [
                      'Charged on settled orders only — a failed or cancelled order costs nothing',
                      'A refunded order credits its commission back',
                      'Debited from your wallet as each order settles',
                    ]
              }
            />
          </Card>
        </div>
      </div>

      {open && (
        <Drawer
          open
          onClose={() => setOpen(null)}
          title={detail.data?.label ?? 'Statement'}
          subtitle={detail.data ? `${detail.data.lines.length} charges` : undefined}
          footer={
            <button type="button" className="btn btn--primary" onClick={() => window.print()}>
              <DownloadIcon size={16} />
              Print statement
            </button>
          }
        >
          {detail.isPending && <RowsSkeleton rows={6} />}

          {detail.error && (
            <Empty error title="Could not load this statement">
              {(detail.error as Error).message}
            </Empty>
          )}

          {detail.data && (
            <>
              <DrawerSection title="Summary">
                <KeyValue
                  rows={[
                    { label: 'Billed to', value: detail.data.billedTo?.name ?? '—' },
                    { label: 'GSTIN', value: detail.data.billedTo?.gstin ?? 'Not added' },
                    { label: 'Commission', value: moneyExact(detail.data.base, detail.data.currency) },
                    { label: 'GST', value: moneyExact(detail.data.tax, detail.data.currency) },
                    { label: 'Total charged', value: moneyExact(detail.data.total, detail.data.currency), kind: 'total' },
                  ]}
                />
              </DrawerSection>

              <DrawerSection title={`Charges (${detail.data.lines.length})`}>
                {detail.data.lines.length === 0 ? (
                  <p className="muted" style={{ fontSize: '.87rem' }}>
                    No commission was charged in this month.
                  </p>
                ) : (
                  <ul className="list list--plain">
                    {detail.data.lines.map((line) => (
                      <li key={line.id} style={{ paddingLeft: 0, paddingRight: 0, alignItems: 'flex-start' }}>
                        <span className="list__text">
                          <strong>{line.orderNumber ?? line.reference}</strong>
                          <small>
                            {dateTime(line.at).split(',')[0]} · on {moneyExact(line.orderValue, detail.data!.currency)}
                            {line.chargeType === 'percent' && ` at ${Number(line.rate)}%`}
                          </small>
                        </span>
                        <span className="list__value">{moneyExact(line.amount, detail.data!.currency)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </DrawerSection>
            </>
          )}
        </Drawer>
      )}
    </>
  )
}

/**
 * The three plans side by side.
 *
 * Switching lives here and only here. The rules are the API's: a paid plan
 * takes a full month from the wallet at once, down to the overdraft floor, so
 * a card whose first month the wallet cannot cover offers the top-up that
 * would, rather than a button that is about to fail.
 */
export function PlansPage(): React.ReactNode {
  const [confirmPlan, setConfirmPlan] = useState<PlanKey | null>(null)
  const queryClient = useQueryClient()

  const billing = useQuery({ queryKey: ['billing'], queryFn: () => api<BillingSummary>('/v1/billing') })
  // For the overdraft floor: what the wallet may spend down to on a plan charge.
  const wallet = useQuery({ queryKey: ['wallet'], queryFn: () => api<WalletSummary>('/v1/wallet') })

  const switchPlan = useMutation({
    mutationFn: (plan: PlanKey) => api<{ changed: boolean; plan: PlanKey }>('/v1/billing/plan', { method: 'PUT', body: { plan } }),
    onSuccess: () => {
      setConfirmPlan(null)
      void queryClient.invalidateQueries({ queryKey: ['billing'] })
      void queryClient.invalidateQueries({ queryKey: ['wallet'] })
    },
  })

  // Shopify embedded admin bills through Shopify only (requirement 1.2.1).
  if (isEmbedded()) return <Navigate to="/wallet/billing" replace />

  const currentKey = billing.data?.plan.key
  const currentPrice = Number(billing.data?.plan.price ?? 0)

  return (
    <>
      <PageHead
        title="Plans"
        subtitle="Every plan has the full checkout. Pick the one that fits how many stores you run."
        crumbs={[{ label: 'Wallet', to: '/wallet' }, { label: 'Plans' }]}
      />

      {switchPlan.error && (
        <Notice kind="error" title="Could not switch plan">
          {(switchPlan.error as Error).message} <Link to="/wallet/add-money">Top up the wallet</Link> and try again.
        </Notice>
      )}

      {billing.error && (
        <Notice kind="error" title="Could not load your plan">
          {(billing.error as Error).message}
        </Notice>
      )}

      <section className="plan-grid">
        {(Object.keys(PLANS) as PlanKey[]).map((key) => {
          const plan = PLANS[key]
          const isCurrent = currentKey === key
          const price = Number(plan.price)
          const confirming = confirmPlan === key
          // How much the wallet is short of this plan's first month, down to the
          // overdraft floor — the same rule the API charges by.
          const shortBy =
            price > 0 && billing.data && wallet.data
              ? Math.ceil(price + Number(wallet.data.minBalance) - Number(billing.data.wallet.balance))
              : 0
          const upgrade = price > currentPrice

          return (
            <article key={key} className={isCurrent ? 'plan-card is-current' : 'plan-card'}>
              <header className="plan-card__head">
                <h2>{plan.name}</h2>
                {isCurrent && <Badge tone="ok">Current plan</Badge>}
              </header>

              <p className="plan-card__price">
                {price > 0 ? money(price) : 'Free'}
                {price > 0 && <small>/month</small>}
              </p>
              <p className="plan-card__desc">{plan.description}</p>

              <div className="plan-card__cta">
                {!billing.data ? (
                  <button type="button" className="btn btn--block" disabled>
                    Loading…
                  </button>
                ) : isCurrent ? (
                  <button type="button" className="btn btn--block" disabled>
                    Your current plan
                  </button>
                ) : shortBy > 0 ? (
                  <Link to={`/wallet/add-money?amount=${shortBy}&plan=${key}`} className="btn btn--primary btn--block">
                    Top up {money(shortBy)} &amp; {upgrade ? 'upgrade' : 'switch'}
                  </Link>
                ) : (
                  <button
                    type="button"
                    className={upgrade ? 'btn btn--primary btn--block' : 'btn btn--block'}
                    disabled={switchPlan.isPending}
                    onClick={() => {
                      if (!confirming) {
                        setConfirmPlan(key)
                        return
                      }

                      setConfirmPlan(null)
                      switchPlan.mutate(key)
                    }}
                  >
                    {confirming
                      ? price > 0
                        ? `Confirm — ${money(price)} now`
                        : 'Confirm switch'
                      : upgrade
                        ? `Upgrade to ${plan.name}`
                        : `Switch to ${plan.name}`}
                  </button>
                )}
              </div>

              <ul className="plan-features plan-card__features" aria-label={`Included in ${plan.name}`}>
                {plan.features.map((feature) => (
                  <li key={feature}>
                    <CheckIcon size={14} />
                    {feature}
                  </li>
                ))}
              </ul>
            </article>
          )
        })}
      </section>

      <p className="muted" style={{ fontSize: '.82rem' }}>
        A paid plan takes a full month from your wallet the moment you switch, and renews from the wallet each month.
        Moving down takes effect at once and is not refunded.
        {billing.data?.renewsAt && <> Your plan renews {dateTime(billing.data.renewsAt).split(',')[0]}.</>}
      </p>
    </>
  )
}
